/**
 * Mixin compartilhado para Observação e Informações Complementares da NFS-e (Issue #388)
 *
 * Centraliza:
 * - Carregamento e tratamento de templates e alíquota por empresa (GET /empresa/:id/templates-e-aliquota)
 * - Tratamento padrão de erro com redirect no 401
 * - Validação de bloqueio obrigatório (sem template ou sem alíquota)
 * - Guard contra empilhamento de toasts de bloqueio (ex.: chamadas consecutivas de montarObs)
 * - Resolução de placeholders e renderização de xDescServ
 * - Distinção explícita entre "nunca editado" (null/undefined) e "editado e limpo de propósito" ("") em xInfComp
 */

const {
  renderTemplate,
  resolverPlaceholders,
  validarConfiguracaoEmissao,
  roundCurrency
} = require("../utils/templateRenderer.js");

const notaObservacaoTemplateMixin = {
  data() {
    return {
      templatesConfig: null,
      bloqueioEmissaoMotivo: null,
      infoComplPreview: null,
      infoComplEditadoManual: false,
      notaObsEditadoManual: false,
      _bloqueioNotificadoTimestamp: 0
    };
  },
  methods: {
    /**
     * Reseta estados temporários de template na abertura do modal
     */
    resetarEstadoTemplate() {
      this.templatesConfig = null;
      this.bloqueioEmissaoMotivo = null;
      this.infoComplPreview = null;
      this.infoComplEditadoManual = false;
      this.notaObsEditadoManual = false;
      this._bloqueioNotificadoTimestamp = 0;
    },

    /**
     * Tratamento padronizado de erro HTTP com notificação e logout/redirecionamento no 401.
     * Centraliza a lógica compartilhada entre componentes e mixin.
     * @param {Error|Object} err
     */
    async tratarErroHttpPadrao(err) {
      if (!this.$httpErrors) return null;
      const error = this.$httpErrors(err);
      if (this.$vs && this.$vs.notify) {
        this.$vs.notify(error);
      }
      if (error && error.code == 401) {
        if (typeof window !== "undefined" && window.localStorage) {
          await window.localStorage.clear();
        }
        if (this.$router) {
          await this.$router.push("/");
        }
      }
      return error;
    },

    /**
     * Busca templates e alíquota vigente da empresa com tratamento padrão de erro e 401
     * @param {number|string} idEmpresa
     * @param {string} [competenciaParam]
     */
    async carregarTemplatesEmpresa(idEmpresa, competenciaParam = null) {
      if (!idEmpresa) {
        this.templatesConfig = null;
        return null;
      }
      try {
        let comp = competenciaParam || (this.nota && this.nota.data_emissao) || null;
        if (comp) {
          comp = String(comp).slice(0, 10);
        }
        let url = `empresa/${idEmpresa}/templates-e-aliquota`;
        if (comp) {
          url += `?competencia=${encodeURIComponent(comp)}`;
        }
        this.templatesConfig = await this.$http.get(url);
        if (
          this.templatesConfig &&
          this.templatesConfig.aliquota_efetiva_vigente !== undefined &&
          this.empresa
        ) {
          this.empresa.aliquota_efetiva_vigente = this.templatesConfig.aliquota_efetiva_vigente;
        }
        return this.templatesConfig;
      } catch (errTemplates) {
        this.templatesConfig = null;
        await this.tratarErroHttpPadrao(errTemplates);
        return null;
      }
    },

    /**
     * Retorna o texto final que será enviado para Descrição do Serviço (serv.xDescServ / nota.obs):
     * - Se o usuário editou no formulário (mesmo que limpo de propósito -> ""), usa nota.obs.
     * - Se nunca editou (null/undefined), renderiza a partir do template configurado.
     * @returns {string}
     */
    obterObsFinal() {
      if (this.nota && this.nota.obs !== undefined && this.nota.obs !== null) {
        return String(this.nota.obs).trim();
      }
      if (
        this.templatesConfig &&
        this.templatesConfig.template_descricao_servico
      ) {
        const valores = this.obterValoresPlaceholders();
        return renderTemplate(
          this.templatesConfig.template_descricao_servico,
          valores
        ).trim();
      }
      return "";
    },

    /**
     * Retorna o texto final que será enviado para Informações Complementares (xInfComp):
     * - Se o usuário editou no formulário (mesmo que limpo de propósito -> ""), usa infoComplPreview.
     * - Se nunca editou (null/undefined), renderiza a partir do template configurado.
     * @returns {string}
     */
    obterInfoComplFinal() {
      if (
        this.infoComplPreview !== undefined &&
        this.infoComplPreview !== null
      ) {
        return String(this.infoComplPreview).trim();
      }
      if (
        this.templatesConfig &&
        this.templatesConfig.template_informacoes_complementares
      ) {
        const valores = this.obterValoresPlaceholders();
        return renderTemplate(
          this.templatesConfig.template_informacoes_complementares,
          valores
        ).trim();
      }
      return "";
    },

    /**
     * Verifica se a empresa atende aos requisitos obrigatórios de template e alíquota,
     * se o texto de benefício fiscal está presente nas informações complementares
     * e se a observação final da nota não foi limpa pelo usuário.
     * @param {Object|number|string} [clienteParam]
     * @returns {boolean}
     */
    verificarRegrasBloqueioTemplate(clienteParam = null, obsPrecalculada = null) {
      const cliente = clienteParam || this.cliente;
      const validacao = validarConfiguracaoEmissao(this.templatesConfig, this.empresa, cliente);
      if (!validacao.valido) {
        this.bloqueioEmissaoMotivo = validacao.motivo;
        return false;
      }

      const textoBeneficio =
        (this.empresa && this.empresa.texto_beneficio_fiscal) ||
        (this.templatesConfig && this.templatesConfig.texto_beneficio_fiscal) ||
        (this.templatesConfig &&
          this.templatesConfig.empresa &&
          this.templatesConfig.empresa.texto_beneficio_fiscal);

      if (textoBeneficio && String(textoBeneficio).trim()) {
        const textoBeneficioStr = String(textoBeneficio).trim();
        const finalInfoCompl = this.obterInfoComplFinal();
        if (!finalInfoCompl || !finalInfoCompl.includes(textoBeneficioStr)) {
          this.bloqueioEmissaoMotivo = `A empresa prestadora possui Benefício Fiscal cadastrado. O campo de Informações Complementares não pode ficar vazio e deve conter obrigatoriamente o texto do benefício fiscal: "${textoBeneficioStr}".`;
          return false;
        }
      }

      if (this.notaObsEditadoManual) {
        const finalObs = (obsPrecalculada !== null && obsPrecalculada !== undefined)
          ? obsPrecalculada
          : this.obterObsFinal();
        if (!finalObs || !String(finalObs).trim()) {
          this.bloqueioEmissaoMotivo = "A Descrição dos Serviços (Observação da NFS-e) é obrigatória e não pode ficar vazia. Preencha o campo de observação antes de emitir a nota.";
          return false;
        }
      }

      this.bloqueioEmissaoMotivo = null;
      return true;
    },

    /**
     * Dispara notificação de bloqueio com guard de 2.5s contra empilhamento de toasts
     */
    notificarBloqueioEmissao() {
      if (!this.bloqueioEmissaoMotivo) return;
      const agora = Date.now();
      if (
        this._bloqueioNotificadoTimestamp &&
        agora - this._bloqueioNotificadoTimestamp < 2500
      ) {
        return;
      }
      this._bloqueioNotificadoTimestamp = agora;
      if (this.$vs && this.$vs.notify) {
        this.$vs.notify({
          title: "Emissão Bloqueada",
          text: this.bloqueioEmissaoMotivo,
          color: "danger",
          time: 8000
        });
      }
    },

    /**
     * Monta o mapa de valores dos placeholders para o contexto atual
     */
    obterValoresPlaceholders() {
      return resolverPlaceholders({
        nota: this.nota,
        contrato: this.contrato,
        fatura: this.fatura,
        empresa: {
          ...this.empresa,
          aliquota_efetiva_vigente: this.templatesConfig
            ? this.templatesConfig.aliquota_efetiva_vigente
            : null,
          competencia: this.templatesConfig
            ? this.templatesConfig.competencia
            : null
        },
        templatesConfig: this.templatesConfig,
        cliente: this.cliente,
        datasVencimento: this.datasVencimento,
        textoDispensaRetencao: this.textoPorcentagensServico
      });
    },

    /**
     * Monta a observação (xDescServ) e o preview de informações complementares (xInfComp)
     */
    async montarObs() {
      if (!this.verificarRegrasBloqueioTemplate()) {
        if (this.nota && !this.notaObsEditadoManual) {
          this.nota.obs = "";
        }
        if (!this.infoComplEditadoManual) {
          this.infoComplPreview = "";
        }
        this.notificarBloqueioEmissao();
        return;
      }

      const valores = this.obterValoresPlaceholders();

      // Só preenche nota.obs a partir do template se o usuário não tiver editado manualmente
      if (!this.notaObsEditadoManual) {
        if (
          this.nota &&
          this.templatesConfig &&
          this.templatesConfig.template_descricao_servico
        ) {
          this.nota.obs = renderTemplate(
            this.templatesConfig.template_descricao_servico,
            valores
          ).trim();
        }
      }

      // Só preenche infoComplPreview a partir do template se o usuário não tiver editado manualmente
      if (!this.infoComplEditadoManual) {
        if (
          this.templatesConfig &&
          this.templatesConfig.template_informacoes_complementares
        ) {
          this.infoComplPreview = renderTemplate(
            this.templatesConfig.template_informacoes_complementares,
            valores
          ).trim();
        } else {
          this.infoComplPreview = "";
        }
      }
    },

    /**
     * Injeta infoCompl.xInfComp no jsonNota respeitando explicitamente se o usuário
     * limpou o campo de propósito ou nunca o editou, e garantindo que benefício fiscal
     * cadastrado não seja descartado silenciosamente.
     */
    injetarInfoComplJsonNota() {
      if (!this.jsonNota || !this.jsonNota.json) return false;

      const textoBeneficio =
        (this.empresa && this.empresa.texto_beneficio_fiscal) ||
        (this.templatesConfig && this.templatesConfig.texto_beneficio_fiscal) ||
        (this.templatesConfig &&
          this.templatesConfig.empresa &&
          this.templatesConfig.empresa.texto_beneficio_fiscal);

      const xInfCompFinal = this.obterInfoComplFinal();

      if (textoBeneficio && String(textoBeneficio).trim()) {
        const textoBeneficioStr = String(textoBeneficio).trim();
        if (!xInfCompFinal || !xInfCompFinal.includes(textoBeneficioStr)) {
          this.bloqueioEmissaoMotivo = `A empresa prestadora possui Benefício Fiscal cadastrado. O campo de Informações Complementares não pode ficar vazio e deve conter obrigatoriamente o texto do benefício fiscal: "${textoBeneficioStr}".`;
          return false;
        }
      }

      if (xInfCompFinal && xInfCompFinal.length > 0) {
        this.jsonNota.json.infoCompl = {
          xInfComp: xInfCompFinal
        };
      } else if (this.jsonNota.json.infoCompl) {
        delete this.jsonNota.json.infoCompl;
      }
      return true;
    },

    /**
     * Valida os parâmetros fiscais da empresa e conteúdo obrigatório antes da emissão.
     * Utilizado tanto na emissão automática quanto no reenvio de RPS.
     * @returns {boolean}
     */
    validarParametrosFiscaisEmpresa() {
      if (!this.empresa) {
        if (this.$vs && this.$vs.notify) {
          this.$vs.notify({
            title: "Empresa não encontrada",
            text: "Não foi possível carregar os dados da empresa prestadora.",
            color: "danger"
          });
        }
        return false;
      }
      const finalObs = this.obterObsFinal();
      if (!this.verificarRegrasBloqueioTemplate(null, finalObs)) {
        if (this.$vs && this.$vs.notify) {
          this.$vs.notify({
            title: "Configuração Fiscal Incompleta",
            text: this.bloqueioEmissaoMotivo,
            color: "danger",
            time: 8000
          });
        }
        return false;
      }
      if (!finalObs || !String(finalObs).trim()) {
        this.bloqueioEmissaoMotivo = "A Descrição dos Serviços (Observação da NFS-e) é obrigatória e não pode ficar vazia. Preencha o campo de observação antes de emitir a nota.";
        if (this.$vs && this.$vs.notify) {
          this.$vs.notify({
            title: "Descrição do Serviço Obrigatória",
            text: this.bloqueioEmissaoMotivo,
            color: "danger",
            time: 8000
          });
        }
        return false;
      }
      const isMeEpp =
        this.empresa.optante_simples_nacional == 1 ||
        this.empresa.optante_simples_nacional == 3;
      if (isMeEpp && !this.empresa.regime_apuracao_simples_nacional) {
        if (this.$vs && this.$vs.notify) {
          this.$vs.notify({
            title: "Regime de Apuração do Simples Nacional obrigatório",
            text: "A empresa prestadora está cadastrada como Optante pelo Simples Nacional (ME/EPP), mas o Regime de Apuração dos Tributos (regApTribSN) não foi informado. Acesse Configurações > Empresa e selecione o Regime de Apuração antes de emitir.",
            color: "danger",
            time: 8000
          });
        }
        return false;
      }
      return true;
    },

    /**
     * Arredondamento monetário bancário (ROUND_HALF_EVEN / Banker's Rounding)
     * unificado com o util templateRenderer.js
     */
    roundCurrencyHalfEven(valor) {
      return roundCurrency(valor);
    }
  }
};

notaObservacaoTemplateMixin.default = notaObservacaoTemplateMixin;
notaObservacaoTemplateMixin.notaObservacaoTemplateMixin = notaObservacaoTemplateMixin;
module.exports = notaObservacaoTemplateMixin;

